import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  List,
  ListItem,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SweepsFirestore } from '../../../services/sweepsFirestore';
import type { SweepCheckedOffReason, SweepFolder, SweepLocation, SweepReader } from '../../../types/sweep';
import { useTheme } from '../../../contexts/ThemeContext';
import AddSweepLocationDialog from './AddSweepLocationDialog';
import {
  formatLocalDateYmd,
  formatSweepScheduledDateLabel,
  sweepIsVisibleInReportSiteBrowse,
} from '../../../utils/sweepSchedule';

function reasonLabel(r: SweepCheckedOffReason | null | undefined): string {
  if (!r) return '';
  if (r === 'completed') return 'Completed';
  if (r === 'store_closed') return 'Store closed';
  return 'Other';
}

export interface SweepDetailDialogProps {
  open: boolean;
  sweepId: string | null;
  onClose: () => void;
  masterOwnerID: string;
  ownerID?: string;
  isAdmin: boolean;
  onSweepDeleted?: () => void;
  onSweepUpdated?: () => void;
}

const SweepDetailDialog: React.FC<SweepDetailDialogProps> = ({
  open,
  sweepId,
  onClose,
  masterOwnerID,
  ownerID,
  isAdmin,
  onSweepDeleted,
  onSweepUpdated,
}) => {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [sweep, setSweep] = useState<SweepLocation | null>(null);
  const [readers, setReaders] = useState<SweepReader[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [addReaderOpen, setAddReaderOpen] = useState(false);
  const [newReaderLabel, setNewReaderLabel] = useState('');
  const [newReaderNotes, setNewReaderNotes] = useState('');
  const [savingReader, setSavingReader] = useState(false);
  const [error, setError] = useState('');
  const [folders, setFolders] = useState<SweepFolder[]>([]);

  const refresh = useCallback(async () => {
    const id = String(sweepId || '').trim();
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const snap = await getDoc(doc(db, 'sweepLocations', id));
      if (!snap.exists()) throw new Error('Sweep location not found.');
      setSweep({ id: snap.id, ...(snap.data() as Record<string, unknown>) } as SweepLocation);
      const list = await SweepsFirestore.listSweepReaders(id);
      setReaders(list);
      const mo = String(masterOwnerID || '').trim();
      if (mo) {
        const fl = await SweepsFirestore.listSweepFolders(mo);
        setFolders(fl);
      } else {
        setFolders([]);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
      setSweep(null);
      setReaders([]);
    } finally {
      setLoading(false);
    }
  }, [sweepId, masterOwnerID]);

  useEffect(() => {
    if (open && sweepId) void refresh();
  }, [open, sweepId, refresh]);

  const onDeleteSweep = async () => {
    const id = String(sweepId || '').trim();
    if (!id || !window.confirm('Delete this sweep location? This cannot be undone.')) return;
    try {
      await SweepsFirestore.deleteSweepLocation(id);
      onSweepDeleted?.();
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  const onAddReader = async () => {
    const id = String(sweepId || '').trim();
    const label = String(newReaderLabel || '').trim();
    if (!id || !label) return;
    setSavingReader(true);
    try {
      await SweepsFirestore.addSweepReader(id, { label, notes: newReaderNotes });
      await SweepsFirestore.updateReaderCountFromReaders(id);
      setNewReaderLabel('');
      setNewReaderNotes('');
      setAddReaderOpen(false);
      await refresh();
      onSweepUpdated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add reader.');
    } finally {
      setSavingReader(false);
    }
  };

  const onDeleteReader = async (readerId: string) => {
    const id = String(sweepId || '').trim();
    if (!id || !window.confirm('Remove this reader entry?')) return;
    try {
      await SweepsFirestore.deleteSweepReader(id, readerId);
      await SweepsFirestore.updateReaderCountFromReaders(id);
      await refresh();
      onSweepUpdated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to remove reader.');
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      background: 'rgba(255,255,255,0.04)',
    },
    '& .MuiInputLabel-root': { color: colors.TextMuted },
    '& .MuiOutlinedInput-input': { color: colors.TextPrimary },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            color: colors.TextPrimary,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color={colors.TextPrimary}>
              {sweep?.name || 'Sweep location'}
            </Typography>
            <Typography variant="body2" color={colors.TextSecondary} sx={{ mt: 0.5 }}>
              {sweep?.address || '—'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.TextMuted }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(34,211,238,0.15)' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : error && !sweep ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <>
              {error ? (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              ) : null}
              <Typography variant="subtitle2" color={colors.TextMuted} sx={{ mb: 0.5 }}>
                Sweep folder
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
                {(() => {
                  const fid = String(sweep?.sweepFolderId ?? '').trim();
                  if (!fid) return '—';
                  const hit = folders.find((f) => f.id === fid);
                  return hit?.name || fid;
                })()}
              </Typography>
              <Typography variant="subtitle2" color={colors.TextMuted} sx={{ mb: 0.5 }}>
                Team #
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
                {typeof sweep?.teamNumber === 'number' && Number.isFinite(sweep.teamNumber)
                  ? String(Math.round(sweep.teamNumber))
                  : '—'}
              </Typography>
              <Typography variant="subtitle2" color={colors.TextMuted} sx={{ mb: 0.5 }}>
                Known info
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
                {sweep?.knownInfo || '—'}
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
                Readers (count):{' '}
                {typeof sweep?.readerCount === 'number' ? sweep.readerCount : '—'}
              </Typography>
              <Typography variant="subtitle2" color={colors.TextMuted} sx={{ mb: 0.5 }}>
                Report Site browse
              </Typography>
              <Typography variant="body2" color={colors.TextSecondary} sx={{ mb: 2 }}>
                {String(sweep?.sweepScheduledDate ?? '').trim()
                  ? `Scheduled visit: ${formatSweepScheduledDateLabel(String(sweep?.sweepScheduledDate))} (browse today only on that day)`
                  : 'No visit date set — appears any day until checked off.'}
                {sweep && !sweep.sweepCheckedOff ? (
                  <>
                    {' '}
                    {sweepIsVisibleInReportSiteBrowse(sweep, formatLocalDateYmd())
                      ? 'Shown in browse today.'
                      : 'Hidden from browse today (wrong visit date).'}
                  </>
                ) : null}
              </Typography>
              {sweep?.sweepCheckedOff ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color={colors.TextMuted} sx={{ mb: 0.5 }}>
                    Check-off
                  </Typography>
                  <Typography variant="body2" color={colors.TextSecondary}>
                    {reasonLabel(sweep.sweepCheckedOffReason)}
                    {sweep.sweepCheckedOffNotes ? ` — ${sweep.sweepCheckedOffNotes}` : ''}
                  </Typography>
                </Box>
              ) : null}

              {isAdmin ? (
                <Box display="flex" gap={1} mb={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setEditOpen(true)}
                    sx={{ borderColor: 'rgba(34,211,238,0.5)', color: '#22d3ee' }}
                  >
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => void onDeleteSweep()}>
                    Delete
                  </Button>
                </Box>
              ) : null}

              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1" fontWeight={600} color={colors.TextPrimary}>
                  Readers
                </Typography>
                {isAdmin ? (
                  <IconButton size="small" onClick={() => setAddReaderOpen(true)} sx={{ color: '#22d3ee' }}>
                    <AddIcon />
                  </IconButton>
                ) : null}
              </Box>
              <List dense sx={{ bgcolor: 'rgba(255,255,255,0.02)', borderRadius: 2 }}>
                {readers.length === 0 ? (
                  <ListItem>
                    <Typography variant="body2" color={colors.TextMuted}>
                      No readers added yet.
                    </Typography>
                  </ListItem>
                ) : (
                  readers.map((r) => (
                    <React.Fragment key={r.id}>
                      <ListItem
                        secondaryAction={
                          isAdmin ? (
                            <IconButton edge="end" size="small" onClick={() => void onDeleteReader(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ) : null
                        }
                        sx={{ alignItems: 'flex-start' }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600} color={colors.TextPrimary}>
                            {r.label}
                          </Typography>
                          {r.notes ? (
                            <Typography variant="caption" color={colors.TextMuted}>
                              {r.notes}
                            </Typography>
                          ) : null}
                        </Box>
                      </ListItem>
                      <Divider component="li" sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                    </React.Fragment>
                  ))
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={onClose} sx={{ color: colors.TextMuted }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <AddSweepLocationDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        masterOwnerID={masterOwnerID}
        ownerID={ownerID}
        folders={folders}
        editing={sweep}
        onSaved={() => {
          void refresh();
          onSweepUpdated?.();
        }}
      />

      <Dialog open={addReaderOpen} onClose={() => setAddReaderOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: colors.TextPrimary }}>Add reader</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Reader label"
            value={newReaderLabel}
            onChange={(e) => setNewReaderLabel(e.target.value)}
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Notes (optional)"
            value={newReaderNotes}
            onChange={(e) => setNewReaderNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={fieldSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddReaderOpen(false)}>Cancel</Button>
          <Button
            onClick={() => void onAddReader()}
            disabled={savingReader || !newReaderLabel.trim()}
            variant="outlined"
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
          >
            {savingReader ? <CircularProgress size={18} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SweepDetailDialog;
